import { Link } from "react-router-dom";
import { ShieldCheck, Search, HeadphonesIcon, Lock, Zap, Award } from "lucide-react";
import ConfigChecker from "./ConfigChecker";
import { GmailIcon, LinkedInIcon, UpworkIcon } from "./icons/SocialIcons";

export default function Footer() {
  return (
    <footer className="bg-navy-gradient text-white pt-24 pb-12 border-t border-white/5 relative">
      {/* Top Wall 3-Color Gradient Separator */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#FF6B4A] via-[#D946EF] to-[#6D28D9]" />

      <div className="container mx-auto px-6">
        {/* API Status Check (Development Only or hidden) */}
        <div className="hidden">
           <ConfigChecker />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-24">
          {/* Company */}
          <div className="space-y-8">
            <Link to="/" className="inline-block" aria-label="Sheun Hub Home">
              <img 
                src="/logo.webp" 
                srcSet="/logo.webp 1x, /logo-2x.webp 2x"
                alt="Sheun Hub" 
                width="112"
                height="112"
                loading="lazy"
                className="h-20 w-auto object-contain drop-shadow-xl" 
              />
            </Link>
            <p className="text-white/70 text-lg leading-relaxed font-serif italic">
              Your Shopify Growth Partner. Building high-converting eCommerce experiences.
            </p>
          </div>

          {/* Services */}
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-white/70 mb-8">Services</p>
            <ul className="space-y-4 font-medium">
              <li><Link to="/services" className="text-white/80 hover:text-[#8B5CF6] transition-colors">Store Setup</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#8B5CF6] transition-colors">Custom Dev</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#8B5CF6] transition-colors">Migrations</Link></li>
              <li><Link to="/services" className="text-white/80 hover:text-[#8B5CF6] transition-colors">SEO & Speed</Link></li>
              <li><Link to="/shopify-seo-sprint" className="text-[#D946EF] font-bold hover:text-white transition-colors">48-Hr SEO Sprint ($250)</Link></li>
              <li><Link to="/shopify-store-audit" className="text-[#FF6B4A] font-bold hover:text-white transition-colors">Shopify Store Audit ($197)</Link></li>
              <li><Link to="/shopify-not-converting" className="text-white/70 hover:text-[#8B5CF6] transition-colors">Fix Low Sales (Tips)</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-white/70 mb-8">Resources</p>
            <ul className="space-y-4 font-medium">
              <li><Link to="/about" className="text-white/80 hover:text-[#8B5CF6] transition-colors">About</Link></li>
              <li><Link to="/portfolio" className="text-white/80 hover:text-[#8B5CF6] transition-colors">Portfolio</Link></li>
              <li><Link to="/blog" className="text-white/80 hover:text-[#8B5CF6] transition-colors">Blog</Link></li>
              <li><Link to="/apply#apply-form" className="text-white/80 hover:text-[#8B5CF6] transition-colors">Apply</Link></li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-white/70 mb-8">Social & Contact</p>
            <ul className="space-y-4 font-medium">
              <li><a href="mailto:sheunhost@gmail.com" className="text-white/80 hover:text-[#8B5CF6] transition-colors flex items-center gap-3"><GmailIcon className="w-5 h-5 text-[#EA4335]" /> Email</a></li>
              <li><a href="https://www.linkedin.com/in/sheun-hub-26b876321" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#8B5CF6] transition-colors flex items-center gap-3"><LinkedInIcon className="w-5 h-5 text-[#0A66C2]" /> LinkedIn</a></li>
              <li><a href="https://www.upwork.com/freelancers/~017eb19011cd354946" target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-[#8B5CF6] transition-colors flex items-center gap-3"><UpworkIcon className="w-5 h-5 text-[#14A800]" /> Upwork</a></li>
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center md:justify-around items-center gap-8 mb-12 py-10 border-y border-white/5 bg-white/[0.02] rounded-3xl mt-8">
          <div className="flex items-center gap-5 group">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-white/5 text-[#8B5CF6] border border-white/10 group-hover:bg-[#6D28D9] group-hover:text-white group-hover:-translate-y-1 transition-all duration-300 shadow-lg">
              <Lock size={24} />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold tracking-wide">256-Bit SSL</span>
              <span className="text-white/50 text-sm font-medium">100% Secure Checkout</span>
            </div>
          </div>
          <div className="flex items-center gap-5 group">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-white/5 text-[#8B5CF6] border border-white/10 group-hover:bg-[#6D28D9] group-hover:text-white group-hover:-translate-y-1 transition-all duration-300 shadow-lg">
              <Zap size={24} />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold tracking-wide">Fast Delivery</span>
              <span className="text-white/50 text-sm font-medium">On-Time Project Turnaround</span>
            </div>
          </div>
          <div className="flex items-center gap-5 group">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-white/5 text-[#8B5CF6] border border-white/10 group-hover:bg-[#6D28D9] group-hover:text-white group-hover:-translate-y-1 transition-all duration-300 shadow-lg">
              <Award size={24} />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-bold tracking-wide">Satisfaction Guarantee</span>
              <span className="text-white/50 text-sm font-medium">5-Star Rated Service</span>
            </div>
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row items-center justify-between gap-8">
          <p className="text-xs text-white/20 font-medium text-center md:text-left">
            © 2026 Sheun Hub. All rights reserved.
          </p>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <span className="bg-white/5 text-[#8B5CF6] text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded-full border border-white/5 flex items-center gap-2">
              <ShieldCheck size={12} /> Verified & Secure
            </span>
            <div className="flex items-center gap-6 text-xs font-medium text-white/20">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
